import { evaluateCubic, type CubicBezier, type EasingFunction } from '../theme/motion';

// Curves of Flutter's CupertinoPageTransition (Flutter 3.38, cupertino/route.dart
// `_CupertinoPageTransitionState._setupAnimation`): the incoming page moves with
// `Curves.fastEaseInToSlowEaseOut` (reverse: its `.flipped`), the page below
// shifts with `linearToEaseOut` (reverse: `easeInToLinear`).

/** CupertinoPageRoute's transition duration; pushed pages (NavigatorView) and the Hero flights over them use it. */
export const PAGE_DURATION_MS = 500;

type Point = readonly [x: number, y: number];

/** Flutter `ThreePointCubic`: two cubic segments joined at `mid`. */
function threePointCubic(a1: Point, b1: Point, mid: Point, a2: Point, b2: Point): EasingFunction {
  const [mx, my] = mid;
  const first: CubicBezier = [a1[0] / mx, a1[1] / my, b1[0] / mx, b1[1] / my];
  const sx = 1 - mx;
  const sy = 1 - my;
  const second: CubicBezier = [(a2[0] - mx) / sx, (a2[1] - my) / sy, (b2[0] - mx) / sx, (b2[1] - my) / sy];
  return (t) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    return t < mx ? evaluateCubic(first, t / mx) * my : evaluateCubic(second, (t - mx) / sx) * sy + my;
  };
}

/** `Curves.fastEaseInToSlowEaseOut` (curves.dart). */
export const fastEaseInToSlowEaseOut: EasingFunction = threePointCubic(
  [0.056, 0.024],
  [0.108, 0.3085],
  [0.198, 0.541],
  [0.3655, 1.0],
  [0.5465, 0.989],
);

/**
 * The incoming page's x offset goes 100 % → 0 with `f(t)` on push. On pop the
 * route animation runs 1 → 0 through `f.flipped(v) = 1 − f(1 − v)`, so in time
 * the offset goes 0 → 100 % with `f` again: one easing serves both directions.
 */
export const pageEase: EasingFunction = fastEaseInToSlowEaseOut;
